import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./AddressModal.module.css";
import { InputItem } from "../InputItem/InputItem";
import { Select } from "../Select/Select";
import { Button } from "../Button/Button";
import Loader from "components/Loader";
import { useTranslation } from "react-i18next";
import environment from "../../environment";

export function AddressModal({
  address,
  isOpen,
  onCancel,
  onValidate,
  onChange,
  title,
  isAdd,
  validationFailed,
  validationErrorsArray,
  loading,
  states,
  apiError,
}) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(
    address?.country ||
      (isAdd
        ? environment.WEB_APP_COUNTRY === "AU"
          ? "Australia"
          : "United States"
        : "")
  );
  const [selectedState, setSelectedState] = useState(address?.state || "");
  const [statePlaceholder, setStatePlaceholder] = useState("-Select State-");
  const [postalCodePlaceholder, setPostalCodePlaceholder] =
    useState("Zip Code");

  const initialAddressData = {
    country: isAdd
      ? environment.WEB_APP_COUNTRY === "AU"
        ? "Australia"
        : "United States"
      : address?.country || "",
    recipient: address?.recipient || "",
    line1: address?.line1 || "",
    line2: address?.line2 || "",
    city: address?.city || "",
    state: address?.state || "",
    postalCode: address?.postalCode || "",
    latitude: address?.latitude || "",
    longitude: address?.longitude || "",
    isVerified: address?.isVerified || 0,
    primary: address?.primary || 0,
    mailing: address?.mailing || 0,
    shipping: address?.shipping || 0,
    billing: address?.billing || 0,
    bitwiseType: address?.bitwiseType || 0,
  };

  const [addressData, setAddressData] = useState(initialAddressData);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (address) {
      const updatedCountry =
        address.country === "US"
          ? "United States"
          : address.country == "JM"
          ? "Jamaica"
          : address.country == "KY"
          ? "Cayman Islands"
          : address.country == "CA"
          ? "Canada"
          : address.country ||
            (isAdd
              ? environment.WEB_APP_COUNTRY === "AU"
                ? "Australia"
                : "United States"
              : "");

      const formattedState = states
        ? states.find((state) => state.abbreviation === address?.state)
        : address?.state;

      setAddressData({
        ...addressData,
        country: updatedCountry,
        recipient: address.recipient || "",
        line1: address.line1 || "",
        line2: address.line2 || "",
        city: address.city || "",
        state: formattedState
          ? `${formattedState.abbreviation} - ${formattedState.state}`
          : address?.state,
        postalCode: address.postalCode || "",
        latitude: address.latitude || "",
        longitude: address.longitude || "",
        isVerified: address.isVerified || 0,
        primary: address.primary || 0,
        mailing: address.mailing || 0,
        shipping: address.shipping || 0,
        billing: address.billing || 0,
        bitwiseType: address.bitwiseType || 0,
      });

      if (formattedState) {
        setSelectedState(
          `${formattedState.abbreviation} - ${formattedState.state}`
        );
      }
      setSelectedCountry(updatedCountry);
    }
  }, [address, states, isAdd]);

  useEffect(() => {
    checkFormValidity();
  }, [addressData, validationFailed]);

  const countryIdName = {
    220: "United States",
    38: "Canada",
    40: "Cayman Islands",
    221: "Jamaica",
    300: "Australia",
    151: "New Zealand",
  };

  const getPlaceholders = (country) => {
    switch (country) {
      case "United States":
        return {
          statePlaceholder: t("Select State"),
          postalCodePlaceholder: t("Zip Code"),
        };
      case "Canada":
        return {
          statePlaceholder: t("Select Province"),
          postalCodePlaceholder: t("Postal Code"),
        };
      case "Cayman Islands":
        return {
          statePlaceholder: t("Select Island"),
          postalCodePlaceholder: t("Postal Code"),
        };
      case "Jamaica":
        return {
          statePlaceholder: t("Select Province/Kingston District"),
          postalCodePlaceholder: t("Postal Code"),
        };
      case "Australia":
        return {
          statePlaceholder: t("Select State"),
          postalCodePlaceholder: t("Postal Code"),
        };
      case "New Zealand":
        return {
          statePlaceholder: t("Select Region"),
          postalCodePlaceholder: t("Postal Code"),
        };
      default:
        return {
          statePlaceholder: t("Select State"),
          postalCodePlaceholder: t("Zip Code"),
        };
    }
  };

  useEffect(() => {
    if (states && selectedCountry) {
      const countryId = Object.keys(countryIdName).find(
        (key) => countryIdName[key] === selectedCountry
      );

      const filteredStates = states
        .filter((state) => state.countryId.toString() === countryId)
        .filter((state) => state.state !== "N/A")
        .map((state) => `${state.abbreviation} - ${state.state}`)
        .sort((a, b) => a.localeCompare(b));
      setStateOptions(filteredStates.filter((state) => state != selectedState));
    }
  }, [states, selectedCountry, selectedState]);

  useEffect(() => {
    const { statePlaceholder, postalCodePlaceholder } =
      getPlaceholders(selectedCountry);
    setStatePlaceholder(statePlaceholder);
    setPostalCodePlaceholder(postalCodePlaceholder);

    if (!stateOptions.includes(selectedState)) {
      setSelectedState("");
    }

    if (initialAddressData.state) {
      let currentStateData = states.find(
        (state) => state.abbreviation == initialAddressData.state
      );

      setSelectedState(
        currentStateData.abbreviation + " - " + currentStateData.state
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (isOpen && isAdd) {
      setSelectedState("");
      setAddressData(initialAddressData);
    }
  }, [isOpen, isAdd]);

  /*
  const countryOptions = [
    "Canada",
    "Cayman Islands",
    "Jamaica",
    "United States",
  ];
  */
  const isANZ = environment.WEB_APP_COUNTRY === "AU";

  const countryOptions = isANZ
    ? ["Australia", "New Zealand"]
    : ["Canada", "Cayman Islands", "Jamaica", "United States"];

  const handleSubmit = () => {
    onValidate(addressData);
  };

  const handleChange = (field, value) => {
    setAddressData((prevState) => {
      const updated = {
        ...prevState,
        [field]: value,
      };
      // validate using updated state
      const isValid = ["line1", "city", "postalCode", "state", "country"].every(
        (key) => updated[key]?.trim() !== ""
      );
      setFormValid(isValid);
      return updated;
    });
    onChange(field, value);
  };

  const checkFormValidity = () => {
    const isValid = ["line1", "city", "postalCode", "state", "country"].every(
      (key) => {
        return addressData[key]?.trim() !== "";
      }
    );
    setFormValid(isValid);
  };

  const getError = (field) => {
    if (validationErrorsArray[field]) {
      return validationErrorsArray[field];
    }
    if (
      validationErrorsArray.suggestions &&
      validationErrorsArray.suggestions.includes(
        "Invalid PO Box or House Number" || "Insufficient Address Information"
      ) &&
      field === "line1"
    ) {
      return validationErrorsArray["line1"];
    }
    return "";
  };

  const handleCancel = () => {
    setAddressData(initialAddressData);
    setSelectedState("");
    setStatePlaceholder("-Select State-");

    onCancel();
  };

  return (
    <>
      {isOpen && (
        <div className={styles.overlay}>
          <div className={styles.container}>
            {loading && (
              <div className={styles.loaderContainer}>
                <Loader color={"#415364"} size={85} />
              </div>
            )}

            {!loading && (
              <>
                {validationFailed && isMobile && (
                  <div className={styles.validationFailedSection}>
                    <div className={styles.validationFailedContainer}>
                      <div className={styles.vFHeader}>
                        {t("Take a second look")}
                      </div>

                      {validationErrorsArray.suggestions && (
                        <div>
                          {validationErrorsArray.suggestions.map(
                            (errorMsg, index) => (
                              <div key={index} className={styles.vFBanner}>
                                {errorMsg}
                              </div>
                            )
                          )}
                        </div>
                      )}
                      <div className={styles.vFFooter}>
                        {t("Make changes to your address")}
                      </div>
                    </div>
                  </div>
                )}
                <div className={styles.innerContainer}>
                  <div className={styles.mainChild}>
                    <div>
                      <h1 className={styles.title}>{title}</h1>
                      {apiError && (
                        <p className={styles.apiError}>{apiError}</p>
                      )}
                    </div>
                    <div className={styles.group}>
                      <div className={styles.formItem}>
                        <InputItem
                          type="text"
                          id="recipient"
                          name="recipient"
                          label={""}
                          value={addressData.recipient}
                          setValue={(value) => handleChange("recipient", value)}
                          placeholder={t("Name")}
                          errors={""}
                          isPhoneNumber={false}
                        />
                      </div>
                      <div className={styles.formSelect}>
                        <Select
                          id="country"
                          name="country"
                          options={countryOptions}
                          onChange={(value) => {
                            handleChange("country", value);
                            setSelectedCountry(value);
                          }}
                          placeholder={selectedCountry || "United States"}
                          value={selectedCountry}
                          disabled={false}
                          dropdownRight={false}
                          isScrollable={true}
                          errors={getError("country")}
                          isStateSelect={false}
                        />
                      </div>

                      <div className={styles.formItem}>
                        <InputItem
                          type="text"
                          id="line1"
                          name="line1"
                          label={""}
                          value={addressData.line1}
                          setValue={(value) => handleChange("line1", value)}
                          placeholder={t("Address")}
                          errors={getError("line1")}
                          isPhoneNumber={false}
                        />
                      </div>
                      <div className={styles.formItem}>
                        <InputItem
                          type="text"
                          id="line2"
                          name="line2"
                          label={""}
                          value={addressData.line2}
                          setValue={(value) => handleChange("line2", value)}
                          placeholder={t("Suite, Apartment, etc.")}
                          errors={getError("line2")}
                          isPhoneNumber={false}
                        />
                      </div>
                      <div className={styles.formItem}>
                        <InputItem
                          type="text"
                          id="city"
                          name="city"
                          label={""}
                          value={addressData.city}
                          setValue={(value) => handleChange("city", value)}
                          placeholder={t("City")}
                          errors={getError("city")}
                          isPhoneNumber={false}
                        />
                      </div>
                      <div className={styles.formSelect}>
                        <Select
                          id="state"
                          name="state"
                          options={stateOptions}
                          onChange={(value) => {
                            handleChange("state", value);
                            setSelectedState(value);
                          }}
                          placeholder={selectedState || statePlaceholder}
                          disabled={
                            !selectedCountry ||
                            selectedCountry === "-Select Country-"
                          }
                          dropdownRight={false}
                          isScrollable={true}
                          errors={getError("state")}
                          isStateSelect={true}
                          className={`${styles.select} ${
                            !selectedCountry ? styles.disabled : ""
                          }`}
                        />
                      </div>
                      <div className={styles.formItem}>
                        <InputItem
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          label={""}
                          value={addressData.postalCode}
                          setValue={(value) =>
                            handleChange("postalCode", value)
                          }
                          placeholder={postalCodePlaceholder}
                          errors={getError("postalCode")}
                          isPhoneNumber={false}
                        />
                      </div>
                    </div>
                    <div className={styles.validate}>
                      <Button
                        buttonText={t("Validate Address")}
                        onClick={handleSubmit}
                        disabled={!formValid && validationFailed}
                        className={`${styles.verificationButton} ${
                          !formValid && validationFailed ? styles.disabled : ""
                        }`}
                      />
                    </div>
                  </div>

                  {validationFailed && !isMobile && (
                    <div className={styles.validationFailedSection}>
                      <div className={styles.validationFailedContainer}>
                        <div className={styles.vFHeader}>
                          {t("Take a second look")}
                        </div>

                        {validationErrorsArray.suggestions && (
                          <div>
                            {validationErrorsArray.suggestions.map(
                              (errorMsg, index) => (
                                <div key={index} className={styles.vFBanner}>
                                  {t(errorMsg)}
                                </div>
                              )
                            )}
                          </div>
                        )}

                        <div className={styles.vFFooter}>
                          {t("Make changes to your address")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.cancel}>
                  <Button
                    buttonText={t("Cancel")}
                    onClick={handleCancel}
                    className={styles.cancelButton}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

AddressModal.propTypes = {
  address: PropTypes.object,
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  onValidate: PropTypes.func,
  onChange: PropTypes.func,
  title: PropTypes.string,
  isAdd: PropTypes.bool,
  validationFailed: PropTypes.bool,
  // validationErrorsArray: PropTypes.array,
  loading: PropTypes.bool,
  states: PropTypes.array,
  apiError: PropTypes.string,
};

export default AddressModal;
